import { DOCUMENT_HTML_ATTRIBUTES } from '../constants';
import { useContext } from 'react';
import DocumentContext from '../contexts/DocumentContext.context';
import useScrollContent from './useScrollContent.hook';
import { holdsDocumentEnd, LARGE_DOCUMENT_STATIC_CLASS } from '../largeDocument';

// Focus the editor when the user clicks the empty area below the content.
const useHandleEditorContentClick = () => {
  const { editor, isEdit } = useContext(DocumentContext);
  const scrollContent = useScrollContent();

  return (event?: { target: EventTarget | null }) => {
    // Only use the fallback when the click happened outside the actual editor.
    // If the click originated inside ProseMirror (for example on an inline block),
    // let ProseMirror keep the native/node selection so the bubble menu can open
    // on the first click.
    if (event?.target instanceof Node && editor.view.dom.contains(event.target)) return;
    // A click in a large document's static half is a request to load that part - StaticElements handles
    // it. Appending a trailing paragraph and focusing the end would jump to the live part instead.
    if (event?.target instanceof Element && event.target.closest(`.${LARGE_DOCUMENT_STATIC_CLASS}`)) return;

    if (isEdit && !editor.isFocused) {
      const { state, view } = editor;
      const { doc } = state;

      const lastNode = doc.lastChild;
      // The editor holds one part, so its end is not the document's - appending there lands in the
      // middle, and inside a landscape section that cuts the section in two on the next save.
      const canAppend = !lastNode?.isTextblock && holdsDocumentEnd();
      const endPos = editor.state.doc.content.size;

      const chain = editor.chain();
      if (canAppend) chain.insertContentAt(endPos, { type: 'paragraph' }, { updateSelection: true }).focus();
      else chain.focus('end');
      chain.run();

      // scroll for better UX
      setTimeout(() => {
        const newLastNode = view.state.doc.lastChild;
        scrollContent({
          value: newLastNode!.attrs[DOCUMENT_HTML_ATTRIBUTES.DEFAULT_ID],
          attribute: DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID,
          noFocus: true,
        });
      }, 0);
    }
  };
};

export default useHandleEditorContentClick;
